import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

export type UseQueryStateOptions<T> = {
  parse?: (value: string) => T | null
  serialize?: (value: T) => string
  defaultValue?: T
}

export type UseQueryStateReturn<T, D> = [
  D extends undefined ? T | null : T,
  (
    value: null | T | ((old: D extends T ? T : T | null) => T | null),
  ) => Promise<boolean>,
]

export function useQueryState<T>(
  key: string,
  options: UseQueryStateOptions<T> & {
    defaultValue: T
  },
): UseQueryStateReturn<
  NonNullable<ReturnType<typeof options.parse>>,
  typeof options.defaultValue
>

export function useQueryState<T>(
  key: string,
  options: UseQueryStateOptions<T>,
): UseQueryStateReturn<NonNullable<ReturnType<typeof options.parse>>, undefined>

export function useQueryState(
  key: string,
  options: {
    defaultValue: string
  },
): UseQueryStateReturn<string, typeof options.defaultValue>

export function useQueryState(
  key: string,
  options: UseQueryStateOptions<string>,
): UseQueryStateReturn<string, undefined>

export function useQueryState(
  key: string,
): UseQueryStateReturn<string, undefined>

export function useQueryState(
  key,
  { parse = (x) => x, serialize = String, defaultValue = undefined } = {
    parse: (x) => x,
    serialize: String,
    defaultValue: undefined,
  } as UseQueryStateOptions<any>,
) {
  const router = useRouter()
  const getValue = useCallback(() => {
    if (typeof window === 'undefined') {
      // Not available in an SSR context
      return router.query[key]
        ? parse(router.query[key] as string)
        : (defaultValue ?? null)
    }
    const query = new URLSearchParams(window.location.search)
    const value = query.get(key)
    return value !== null ? parse(value) : null
  }, [])

  // Update the state value only when the relevant key changes.
  // Because we're not calling getValue in the function argument
  // of React.useMemo, but instead using it as the function to call,
  // there is no need to pass it in the dependency array.
  const value = useMemo(getValue, [router.query[key]])

  const update = useCallback(
    (value) => {
      let _a, _b
      const isUpdaterFunction = (input) => {
        return typeof input === 'function'
      }
      const oldValue =
        (_b =
          (_a = getValue()) !== null && _a !== void 0 ? _a : defaultValue) !==
          null && _b !== void 0
          ? _b
          : null
      const newValue = isUpdaterFunction(value) ? value(oldValue) : value

      const url = new URL(window.location.href)
      if (newValue === null) {
        // Don't leave value-less keys hanging
        url.searchParams.delete(key)
      } else {
        url.searchParams.set(key, serialize(newValue))
      }
      router.replace(url, undefined, { shallow: true })
    },
    [key],
  )
  let _a: any
  return [
    (_a = value !== null && value !== void 0 ? value : defaultValue) !== null &&
    _a !== void 0
      ? _a
      : null,
    update,
  ]
}

export default useQueryState
